import type { Node } from '@atlaskit/editor-prosemirror/model';
import { getMauiIdFromAdfNode } from '@atlassian/malleable-ui-components/editor-remix/remix-ids';

import type { CwrRemixTarget } from '../malleableUiPluginType';
import { findMedia } from './remix-result-tracker';

const isMauiExtension = (node: Node): boolean =>
	node.type.name === 'extension' && node.attrs.extensionKey === 'native-embed:maui';

const getFileMediaId = (node: Node | null | undefined): string | undefined =>
	node?.type.name === 'media' && node.attrs.type === 'file' && typeof node.attrs.id === 'string'
		? node.attrs.id
		: undefined;

/**
 * Resolves the visual a CWR `replaceNode` is about to overwrite.
 * Media uses the tracker's localId (the `mediaSingle` wrapper when unique), so the
 * replacement key matches the tracked result whichever node the agent targets.
 */
export const findCwrRemixTarget = (doc: Node, localId: string): CwrRemixTarget | undefined => {
	let target: CwrRemixTarget | undefined;
	doc.descendants((node) => {
		if (node.attrs.localId !== localId) {
			return true;
		}

		if (isMauiExtension(node)) {
			const mauiId = getMauiIdFromAdfNode(node);
			if (mauiId) {
				target = { identity: { attribute: 'mauiVersion', localId, value: mauiId }, localId };
			}
			return false;
		}

		const mediaId = getFileMediaId(node.type.name === 'mediaSingle' ? node.firstChild : node);
		if (mediaId) {
			target = {
				identity: { attribute: 'id', value: mediaId },
				localId: findMedia(doc, mediaId).localId ?? localId,
			};
		}
		return false;
	});
	return target;
};

/** Returns `attribute:value` keys for every MAUI app and media node in the document. */
export const getCwrRemixIdentityKeys = (doc: Node): Set<string> => {
	const keys = new Set<string>();
	doc.descendants((node) => {
		if (isMauiExtension(node)) {
			const mauiId = getMauiIdFromAdfNode(node);
			if (mauiId) {
				keys.add(`mauiVersion:${mauiId}`);
			}
		}
		if (node.type.name === 'media' && typeof node.attrs.id === 'string') {
			keys.add(`id:${node.attrs.id}`);
		}
		return true;
	});
	return keys;
};
